import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { canMutateCrm } from '../lib/permissions';
import CreateLeadModal from '../components/CreateLeadModal';

const human = (value='') => value.replaceAll('_',' ').replace(/\b\w/g, m=>m.toUpperCase());
const fmt = (value) => value ? new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',year:'numeric'}).format(new Date(value)) : '—';
const instagramSearchValue = value => {
  const normalized=String(value||'').trim().replace(/^@+/, '');
  return normalized?`@${normalized}`:'';
};

function MatchSubmissionModal({ item, workspaceId, canCreateLead, onClose, onMatched }) {
  const applicantInstagram=item.qualification_submissions?.applicant_instagram||'';
  const applicantSearch=instagramSearchValue(applicantInstagram);
  const [query,setQuery]=useState(applicantSearch);
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState('');
  const [error,setError]=useState('');
  const [creatingLead,setCreatingLead]=useState(false);

  useEffect(()=>{setQuery(applicantSearch)},[applicantSearch,item.id]);

  const loadRows=useCallback(async()=>{
    setLoading(true); setError('');
    const {data,error:loadError}=await supabase
      .from('sales_cycles')
      .select('id,lead_id,started_at,journey_stage,qualification_status,sales_outcome,leads:leads!sales_cycles_workspace_id_lead_id_fkey(id,name,email,instagram_handle,deleted_at,archive_state,validity),offers:offers!sales_cycles_workspace_id_offer_id_fkey(name)')
      .eq('workspace_id',workspaceId)
      .eq('sales_outcome','open')
      .order('started_at',{ascending:false});
    if(loadError)setError(loadError.message);
    else setRows((data||[]).filter(r=>r.leads && !r.leads.deleted_at && r.leads.validity==='valid'));
    setLoading(false);
  },[workspaceId]);

  useEffect(()=>{let active=true;(async()=>{if(!active)return;await loadRows()})();return()=>{active=false};},[loadRows]);

  const filtered=useMemo(()=>{
    const q=query.trim().toLowerCase();
    if(!q)return rows;
    const normalizedQuery=q.replace(/^@+/, '');
    return rows.filter(r=>[
      r.leads?.name,
      r.leads?.email,
      r.leads?.instagram_handle,
      String(r.leads?.instagram_handle||'').replace(/^@+/, ''),
    ].some(v=>(v||'').toLowerCase().includes(normalizedQuery)));
  },[rows,query]);

  async function match(row){
    setBusy(row.id); setError('');
    const {error:rpcError}=await supabase.rpc('match_qualification_submission',{
      p_submission_id:item.qualification_submission_id,
      p_sales_cycle_id:row.id,
    });
    if(rpcError){setError(rpcError.message);setBusy('');return;}
    await onMatched?.();
    onClose();
  }

  async function createdAndMatch(created){
    const leadId=created?.lead_id||created?.id;
    if(!leadId){setCreatingLead(false);setError('Lead was created, but the new Lead ID could not be resolved. Refresh and match it manually.');await loadRows();return;}
    setBusy('created');setError('');
    const {data:cycle,error:cycleError}=await supabase
      .from('sales_cycles')
      .select('id')
      .eq('workspace_id',workspaceId)
      .eq('lead_id',leadId)
      .eq('sales_outcome','open')
      .order('started_at',{ascending:false})
      .limit(1)
      .maybeSingle();
    if(cycleError||!cycle?.id){
      setBusy('');setCreatingLead(false);
      setError(`Lead was created, but its open Sales Cycle could not be matched automatically${cycleError?.message?`: ${cycleError.message}`:'.'}`);
      await loadRows();
      return;
    }
    const {error:matchError}=await supabase.rpc('match_qualification_submission',{
      p_submission_id:item.qualification_submission_id,
      p_sales_cycle_id:cycle.id,
    });
    setBusy('');
    if(matchError){
      setCreatingLead(false);
      setError(`Lead was created, but the form could not be matched automatically: ${matchError.message}`);
      await loadRows();
      return;
    }
    await onMatched?.();
    setCreatingLead(false);
    onClose();
  }

  const showCreateLead=canCreateLead&&!loading&&query.trim().length>0&&filtered.length===0;

  if(creatingLead){
    return <CreateLeadModal
      open
      defaultMotion="outbound"
      initialSearch={applicantSearch}
      onClose={()=>setCreatingLead(false)}
      onCreated={createdAndMatch}
    />;
  }

  return <div className="modal-backdrop" role="presentation">
    <div className="modal-card attention-match-modal" role="dialog" aria-modal="true">
      <div className="modal-head"><div><h2>Match Qualification Form</h2><p>Choose the existing Lead this form belongs to, or create a new Lead and match it immediately.</p></div><button className="icon-button" onClick={onClose} aria-label="Close">×</button></div>
      <div className="source-summary">
        <div><span>Applicant</span><strong>{item.qualification_submissions?.applicant_instagram || item.qualification_submissions?.applicant_email || 'Unmatched applicant'}</strong></div>
        <div><span>Calculated result</span><strong>{human(item.qualification_submissions?.calculated_status || 'pending')}</strong></div>
      </div>
      <label className="field-label">Search existing Leads</label>
      <div className="match-search-control">
        <input className="text-input" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Name, email or Instagram handle" autoFocus/>
        {showCreateLead&&<div className="lead-search-create-menu match-search-create-menu">
          <div className="lead-search-create-copy"><strong>No matching lead found</strong><span>No existing lead matches <b>{query.trim()}</b>.</span></div>
          <button type="button" className="lead-search-create-action" disabled={!!busy} onClick={()=>setCreatingLead(true)}><span><strong>+ Create new lead</strong><small>Use this applicant's Instagram in the new lead form</small></span><span>→</span></button>
        </div>}
      </div>
      {error&&<div className="error-message">{error}</div>}
      <div className="match-list">
        {loading?<div className="compact-empty">Loading open Sales Cycles…</div>:
        filtered.length===0?<div className="compact-empty">{rows.length===0?'There are no existing open Leads to match.':'No matching Lead found.'}</div>:
        filtered.map(row=><div className="match-row" key={row.id}>
          <div><strong>{row.leads.name}</strong><span>{row.leads.instagram_handle||'No Instagram'}{row.leads.email?` · ${row.leads.email}`:''}</span><small>{row.offers?.name||'Offer'} · {human(row.journey_stage)}</small></div>
          <button className="primary-button compact no-margin" disabled={!!busy} onClick={()=>match(row)}>{busy===row.id?'Matching…':'Match Form'}</button>
        </div>)}
      </div>
      <div className="modal-foot-note">Matching applies the stored qualification result to the selected or newly created Lead when safe.</div>
    </div>
  </div>;
}

function QualificationReviewActions({ item, onDone }) {
  const [busy,setBusy]=useState('');
  const [error,setError]=useState('');
  const cycleId=item.sales_cycle_id;
  const submissionId=item.qualification_submission_id;

  async function run(label, fn){
    setBusy(label); setError('');
    try{
      const {error:rpcError}=await fn();
      if(rpcError) throw rpcError;
      await onDone?.();
    }catch(e){setError(e.message||String(e));}
    finally{setBusy('');}
  }

  const type=item.attention_type;
  if(type==='qualification_maybe' && cycleId){
    return <div className="review-action-stack">
      <div className="attention-actions">
        <button className="primary-button compact no-margin" disabled={!!busy} onClick={()=>run('qualified',()=>supabase.rpc('set_qualification_status',{p_sales_cycle_id:cycleId,p_status:'qualified'}))}>{busy==='qualified'?'Saving…':'Mark Qualified'}</button>
        <button className="secondary-button inline-button" disabled={!!busy} onClick={()=>run('not_qualified',()=>supabase.rpc('set_qualification_status',{p_sales_cycle_id:cycleId,p_status:'not_qualified'}))}>{busy==='not_qualified'?'Saving…':'Mark Not Qualified'}</button>
      </div>
      {error&&<div className="inline-error">{error}</div>}
    </div>;
  }

  if(type==='qualification_repeat_submission' && submissionId){
    return <div className="review-action-stack">
      <div className="attention-actions">
        <button className="primary-button compact no-margin" disabled={!!busy} onClick={()=>run('apply',()=>supabase.rpc('review_repeat_qualification_submission',{p_submission_id:submissionId,p_apply:true}))}>{busy==='apply'?'Applying…':'Apply New Result'}</button>
        <button className="secondary-button inline-button" disabled={!!busy} onClick={()=>run('keep',()=>supabase.rpc('review_repeat_qualification_submission',{p_submission_id:submissionId,p_apply:false}))}>{busy==='keep'?'Saving…':'Keep Current Result'}</button>
      </div>
      {error&&<div className="inline-error">{error}</div>}
    </div>;
  }

  if(type==='qualification_stage_mismatch' && cycleId){
    return <div className="review-action-stack">
      <button className="primary-button compact no-margin" disabled={!!busy} onClick={()=>run('form_sent',()=>supabase.rpc('mark_form_sent',{p_sales_cycle_id:cycleId}))}>{busy==='form_sent'?'Updating…':'Mark Form Sent'}</button>
      {error&&<div className="inline-error">{error}</div>}
    </div>;
  }

  return null;
}

export default function AttentionPage(){
  const {workspace,role}=useAuth();
  const [searchParams,setSearchParams]=useSearchParams();
  const targetItemId=searchParams.get('item');
  const queueFilter=searchParams.get('filter')||'all';
  const [items,setItems]=useState([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [matching,setMatching]=useState(null);
  const canCreateLead=canMutateCrm(role);

  const load=useCallback(async({silent=false}={})=>{
    if(!silent)setLoading(true);
    setError('');
    const {data,error:loadError}=await supabase
      .from('attention_items')
      .select('id,attention_type,status,opened_at,lead_id,sales_cycle_id,qualification_submission_id,metadata,leads:leads!attention_items_workspace_id_lead_id_fkey(id,name,instagram_handle),qualification_submissions:qualification_submissions!attention_items_workspace_id_qualification_submission_id_fkey(id,applicant_email,applicant_instagram,calculated_status,match_status,review_reasons)')
      .eq('workspace_id',workspace.id)
      .eq('status','open')
      .order('opened_at',{ascending:true});
    if(loadError)setError(loadError.message);else setItems(data||[]);
    if(!silent)setLoading(false);
  },[workspace.id]);

  useEffect(()=>{
    load();
    const timer=window.setInterval(()=>load({silent:true}),5000);
    const refresh=()=>load({silent:true});
    window.addEventListener('focus',refresh);
    document.addEventListener('visibilitychange',refresh);
    return()=>{
      window.clearInterval(timer);
      window.removeEventListener('focus',refresh);
      document.removeEventListener('visibilitychange',refresh);
    };
  },[load]);

  useEffect(()=>{
    if(!targetItemId||!items.some(item=>item.id===targetItemId))return;
    const node=document.getElementById(`attention-${targetItemId}`);
    if(!node)return;
    node.scrollIntoView({behavior:'smooth',block:'center'});
    node.focus({preventScroll:true});
  },[targetItemId,items]);

  useEffect(()=>{
    if(!searchParams.get('queue'))return;
    document.getElementById('attention-work-queue')?.scrollIntoView({behavior:'smooth',block:'start'});
  },[searchParams]);

  function openQueue(filter='all'){
    const next=new URLSearchParams(searchParams);
    next.set('queue','1');
    filter==='all'?next.delete('filter'):next.set('filter',filter);
    next.delete('item');
    setSearchParams(next,{replace:false});
  }

  const qualificationCount=items.filter(i=>i.qualification_submission_id).length;
  const visibleItems=queueFilter==='qualification'?items.filter(i=>i.qualification_submission_id):items;

  return <section className="page-stack">
    <div className="page-head-row"><div><h1 className="page-title">Needs Attention</h1><p className="page-description">Only items that require a human decision or correction belong here.</p></div><button className="secondary-button" onClick={()=>load()}>Refresh</button></div>
    {error&&<div className="error-message">{error}</div>}
    <div className="metric-grid attention-metrics">
      <button type="button" className="metric-card metric-card-link" onClick={()=>openQueue('all')}><span>Open Items</span><strong>{items.length}</strong><small>Human action required</small></button>
      <button type="button" className="metric-card metric-card-link" onClick={()=>openQueue('qualification')}><span>Qualification</span><strong>{qualificationCount}</strong><small>Forms or review cases</small></button>
    </div>
    <div className="panel" id="attention-work-queue">
      <div className="panel-head"><h2>{queueFilter==='qualification'?'Qualification Queue':'Work Queue'}</h2><span className="pill">{visibleItems.length} open</span></div>
      {loading?<div className="compact-empty">Loading Needs Attention…</div>:visibleItems.length===0?<div className="compact-empty">{queueFilter==='qualification'?'No qualification items need attention.':'Nothing needs attention.'}</div>:visibleItems.map(item=>{
        const q=item.qualification_submissions;
        const label=item.leads?.name||q?.applicant_instagram||q?.applicant_email||'Unmatched qualification';
        const isUnmatched=['qualification_unmatched','qualification_identifier_conflict','qualification_match_review'].includes(item.attention_type)&&item.qualification_submission_id;
        const isTarget=item.id===targetItemId;
        return <div id={`attention-${item.id}`} tabIndex={-1} className={`attention-row${isTarget?' attention-row-targeted':''}`} key={item.id}>
          <div className="attention-main"><div className="attention-title"><strong>{label}</strong><span className="attention-type">{human(item.attention_type)}</span></div><div className="attention-meta">{q?.calculated_status&&<span>Calculated: {human(q.calculated_status)}</span>}<span>Opened {fmt(item.opened_at)}</span></div></div>
          <div className="attention-actions-wrap">
            <div className="attention-actions">
              {item.lead_id&&<Link className="secondary-button inline-button" to={`/leads/${item.lead_id}`}>Open Lead</Link>}
              {isUnmatched&&<button className="primary-button compact no-margin" onClick={()=>setMatching(item)}>Match to Lead</button>}
            </div>
            <QualificationReviewActions item={item} onDone={()=>load({silent:true})}/>
          </div>
        </div>
      })}
    </div>
    {matching&&<MatchSubmissionModal item={matching} workspaceId={workspace.id} canCreateLead={canCreateLead} onClose={()=>setMatching(null)} onMatched={()=>load({silent:true})}/>}    
  </section>;
}